/**
 * postProcessAddress — repair anomalies in AI-returned address parse before USPS.
 *
 * Empirically observed anomalies across 66 state-DL samples (2026-04-23 analysis):
 *
 *   Wyoming street/zip swap (rare but reproducible):
 *     AI.valueAddress = { streetAddress: '', city: 'ANYSTREET\nANYTOWN',
 *                         state: 'WY', postalCode: '123' }
 *     Root cause: model treated the leading street number as ZIP; ran street
 *     name + actual city into newline-joined city field.
 *     Fix: if street is empty AND city contains a newline AND postalCode is
 *     a short numeric (<5 digits), flag for officer review with raw content;
 *     return an { kind: 'anomaly' } shape so caller can skip USPS and show
 *     the raw string for manual split.
 *
 *   West Virginia state-in-zip (rare):
 *     AI.valueAddress = { streetAddress: '1 RHODODENDRON ROAD',
 *                         city: 'MOUNTAIN CITY', state: '',
 *                         postalCode: 'WV-23959' }
 *     Fix: regex-split the postalCode; extract state + clean zip.
 *
 *   ZIP format inconsistencies (common):
 *     - Stub "-0000" suffix (AI padded a 5-digit ZIP to 9-digit form): strip
 *     - 9 digits no dash (SC DL '123451111'): insert dash after position 5
 *
 *   Directional-prefixed city ambiguity (Minneapolis MN sample, 2026-04-24):
 *     AI.valueAddress = { streetAddress: '123 MAIN STREET',
 *                         city: 'NORTHWEST MINNEAPOLIS', state: 'MN',
 *                         postalCode: '55401' }
 *     Root cause: the street post-directional ("NW") printed after the city
 *     line on the DL got concatenated into the city field as "Northwest".
 *     BUT many legitimate US cities ARE directionally prefixed
 *     (North Kansas City, West Plains, South Bend, East Lansing, etc.), so
 *     we can't unconditionally strip. Instead: DETECT ambiguity, surface
 *     both interpretations to the officer as a disambiguation choice, and
 *     flag a recommendation based on a small allowlist of known
 *     directionally-named cities.
 *
 * Runs in the pipeline BEFORE USPS standardization. USPS will further canon
 * addresses; titleCase applies after USPS. See docs/port-design/people-form-port.md §2.
 */

export interface DirectionalAmbiguity {
  /** The directional word as it appeared in the city ("Northwest", "NW", ...). */
  leadingWord: string;
  /** City as AI returned it (title-cased), e.g. "Northwest Minneapolis". */
  cityAsIs: string;
  /** City with the leading directional stripped, e.g. "Minneapolis". */
  alternateCity: string;
  /** Street with the directional appended in 2-char postal form, e.g. "123 Main St NW". */
  alternateAddress: string;
  /**
   * 'keep-as-is'      — city matches a known legitimately-directional city
   *                     (West Plains MO, North Kansas City MO, etc.)
   * 'move-to-street'  — no allowlist match; best guess is the directional
   *                     belongs on the street line.
   */
  recommendedChoice: 'keep-as-is' | 'move-to-street';
}

/**
 * Street-suffix bleed detection — sibling of DirectionalAmbiguity.
 * Surfaces when the AI returned a city beginning with (or smushed against) a
 * street-suffix token like "St", "Ave", "Blvd", suggesting the suffix from
 * the previous line's street address bled into the city field.
 *
 * Triggering MS-DL sample (2026-04-24): DL printed "123 MAIN ST\nJACKSON, MS"
 * — Azure DI returned street="123 MAIN", city="STJACKSON" (smushed, no
 * space). Our titleCase rendered "Stjackson", garbage value displayed.
 */
export interface StreetSuffixAmbiguity {
  /** The suffix token as it appeared ("ST", "St", "Ave", "Blvd"...). */
  leadingWord: string;
  /** True when the suffix and city were one token (no whitespace separator)
   *  — the high-confidence "this was a parser glitch" indicator. */
  smushed: boolean;
  /** City as AI returned it, capitalization-normalized (e.g. "Stjackson"). */
  cityAsIs: string;
  /** City with the leading suffix stripped (e.g. "Jackson"). */
  alternateCity: string;
  /** Street with the suffix appended in canonical form
   *  (e.g. "123 Main St" — note Title-Case suffix). */
  alternateAddress: string;
  /**
   * 'keep-as-is'      — city matches a known "Saint X" / legitimate-prefix city
   *                     (St. Louis, St. Joseph, etc.)
   * 'move-to-street'  — most likely AI bled the street suffix into the city;
   *                     move it back. Default for non-allowlisted cities.
   */
  recommendedChoice: 'keep-as-is' | 'move-to-street';
}

export interface ParsedAddress {
  kind: 'parsed';
  streetAddress: string;
  city: string;
  state: string;
  postalCode: string;
  countryRegion: string;
  /** Present when the city line starts with a directional word and both
   *  interpretations (city-as-is vs move-to-street) are plausible. */
  directionalAmbiguity?: DirectionalAmbiguity;
  /** Present when the city line starts with (or is smushed against) a street
   *  suffix token, suggesting the suffix from the prior line's street bled
   *  into the city field. */
  streetSuffixAmbiguity?: StreetSuffixAmbiguity;
}

export interface AddressAnomaly {
  kind: 'anomaly';
  reason: string;
  rawContent: string;
}

export type PostProcessResult = ParsedAddress | AddressAnomaly;

export interface AiAddressInput {
  streetAddress?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  countryRegion?: string | null;
  /** The raw uncropped content string from the AI (the address as printed, unparsed). */
  rawContent?: string | null;
}

function normalizeZip(zip: string | undefined | null): string {
  if (!zip) return '';
  let z = zip.trim();
  // Strip stub padding: "65401-0000" -> "65401"
  z = z.replace(/-0000$/, '');
  // Insert dash on 9-digit-no-dash: "654011234" -> "65401-1234"
  if (/^\d{9}$/.test(z)) {
    z = `${z.slice(0, 5)}-${z.slice(5)}`;
  }
  return z;
}

// ---------------------------------------------------------------------------
// Directional-prefix detection
// ---------------------------------------------------------------------------

/** Map of directional words -> their 2-char postal abbreviation (uppercased). */
const DIRECTIONAL_WORD_TO_ABBR: Record<string, string> = {
  north: 'N',
  south: 'S',
  east: 'E',
  west: 'W',
  northeast: 'NE',
  northwest: 'NW',
  southeast: 'SE',
  southwest: 'SW',
  ne: 'NE',
  nw: 'NW',
  se: 'SE',
  sw: 'SW',
  n: 'N',
  s: 'S',
  e: 'E',
  w: 'W',
};

/**
 * Known legitimately-directional US city names (title-cased, exact match).
 * Not exhaustive — just the common cases Bill flagged plus a handful of
 * nearby obvious ones. Expanded opportunistically as new samples surface.
 */
const KNOWN_DIRECTIONAL_CITIES: ReadonlySet<string> = new Set([
  // West-prefixed
  'West Sullivan',
  'West Plains',
  'West Alton',
  'West Line',
  'West Palm Beach',
  'West Lafayette',
  'West Memphis',
  'West Hollywood',
  'West Jordan',
  'West Valley City',
  // North-prefixed
  'North Kansas City',
  'North Little Rock',
  'North Las Vegas',
  'Northfield',
  'North Bend',
  'North Charleston',
  'North Miami',
  'North Platte',
  // South-prefixed
  'South Gate',
  'South Bend',
  'Southfield',
  'South Fulton',
  'Southaven',
  'South San Francisco',
  // East-prefixed
  'East Lansing',
  'East St. Louis',
  'East Saint Louis',
  'East Prairie',
  'Eastpointe',
  'Eastampton',
  'East Orange',
  'East Providence',
]);

/** Simple title-case for city compare; does NOT expand abbreviations. */
function titleCaseBasic(s: string): string {
  return s
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/**
 * If the city starts with a directional word, return the detected word + the
 * remainder of the city. Matches e.g.:
 *   "Northwest Minneapolis" -> { word: 'Northwest', rest: 'Minneapolis' }
 *   "NW Minneapolis"        -> { word: 'NW',        rest: 'Minneapolis' }
 *   "WEST PLAINS"           -> { word: 'West',      rest: 'Plains' }
 * Returns null for glued names like "Northfield" / "Eastpointe" / "Southaven"
 * where there's no space separator — those are unambiguous city names.
 */
function detectLeadingDirectional(city: string): { word: string; rest: string } | null {
  const trimmed = city.trim();
  if (!trimmed) return null;
  // Pattern: word boundary, one of the directional tokens, space, rest
  const m = trimmed.match(/^(north|south|east|west|northeast|northwest|southeast|southwest|ne|nw|se|sw|n|s|e|w)\s+(\S.*)$/i);
  if (!m) return null;
  const word = m[1]!;
  const rest = m[2]!.trim();
  // A single-letter directional ("N Minneapolis") is plausible but 1-char
  // would also match random middle initials. Still safest to treat as a
  // directional at the START of a city field.
  return { word, rest };
}

function buildAlternateAddress(street: string, directionalWord: string): string {
  const abbr = DIRECTIONAL_WORD_TO_ABBR[directionalWord.toLowerCase()] ?? directionalWord.toUpperCase();
  const trimmed = street.trim();
  if (!trimmed) return abbr;
  return `${trimmed} ${abbr}`;
}

// ---------------------------------------------------------------------------
// Street-suffix-prefix detection (sibling of directional)
// ---------------------------------------------------------------------------

/** Canonical Title-Case form of each street suffix token, keyed by lowercase
 *  input form. Used both for the suffix-attached-to-street alternate AND for
 *  presenting the suffix back to the FSR in a familiar form. */
const STREET_SUFFIX_CANONICAL: Record<string, string> = {
  st: 'St', street: 'Street',
  ave: 'Ave', avenue: 'Avenue',
  blvd: 'Blvd', boulevard: 'Boulevard',
  rd: 'Rd', road: 'Road',
  ln: 'Ln', lane: 'Lane',
  ct: 'Ct', court: 'Court',
  pl: 'Pl', place: 'Place',
  dr: 'Dr', drive: 'Drive',
  way: 'Way',
  pkwy: 'Pkwy', parkway: 'Parkway',
  ter: 'Ter', terrace: 'Terrace',
  cir: 'Cir', circle: 'Circle',
  trl: 'Trl', trail: 'Trail',
  hwy: 'Hwy', highway: 'Highway',
};

/** Smush-detection token list — longer tokens first so "street" wins over "st". */
const STREET_SUFFIX_TOKENS_SMUSH_ORDER: ReadonlyArray<string> = [
  'boulevard', 'parkway', 'terrace', 'highway', 'street', 'avenue', 'circle', 'trail',
  'place', 'drive', 'court', 'road', 'lane', 'pkwy', 'blvd', 'hwy', 'cir', 'trl', 'ter',
  'ave', 'way', 'st', 'rd', 'ln', 'ct', 'pl', 'dr',
];

/**
 * Known "Saint X" cities — when the bleed detector sees "St"/"ST" + a name in
 * this set, recommend keep-as-is rather than move-to-street. Compare on
 * lowercase, no-period.
 */
const KNOWN_SAINT_CITIES: ReadonlySet<string> = new Set([
  'st louis', 'stlouis',
  'st joseph', 'stjoseph',
  'st charles', 'stcharles',
  'st paul', 'stpaul',
  'st petersburg', 'stpetersburg',
  'st augustine', 'staugustine',
  'st cloud', 'stcloud',
  'st george', 'stgeorge',
  'st marys', 'stmarys',
  'st peters', 'stpeters',
  'st ann', 'stann',
  'st francis', 'stfrancis',
  'st johns', 'stjohns',
  'st clair shores', 'stclairshores',
  'st clair', 'stclair',
]);

/**
 * If the city begins with (or IS smushed against) a recognized street-suffix
 * token, return the detected token + remainder + smushed flag.
 *
 * Matches:
 *   "ST JACKSON"   -> { word: 'ST',   rest: 'JACKSON', smushed: false }
 *   "STJACKSON"    -> { word: 'ST',   rest: 'JACKSON', smushed: true  }
 *   "St. Jackson"  -> { word: 'St',   rest: 'Jackson', smushed: false }
 *   "BlvdMyTown"   -> { word: 'Blvd', rest: 'MyTown',  smushed: true  }
 *
 * Returns null when the city has no leading suffix token, OR when the
 * remainder is too short to be a plausible city (< 3 chars).
 */
function detectLeadingStreetSuffix(
  city: string,
): { word: string; rest: string; smushed: boolean } | null {
  const trimmed = city.trim();
  if (!trimmed) return null;

  // Pattern 1: separated by space, period, or comma.
  const sepMatch = trimmed.match(
    /^(st|street|ave|avenue|blvd|boulevard|rd|road|ln|lane|ct|court|pl|place|dr|drive|way|pkwy|parkway|ter|terrace|cir|circle|trl|trail|hwy|highway)\.?[,\s]+(\S.*)$/i,
  );
  if (sepMatch) {
    const rest = sepMatch[2]!.trim();
    if (rest.length >= 3) {
      return { word: sepMatch[1]!, rest, smushed: false };
    }
  }

  // Pattern 2: smushed (no separator). Iterate longest-first so "street"
  // matches "STREETJACKSON" before "st" does.
  const lower = trimmed.toLowerCase();
  for (const token of STREET_SUFFIX_TOKENS_SMUSH_ORDER) {
    if (lower.startsWith(token) && lower.length > token.length) {
      const rest = trimmed.slice(token.length);
      if (rest.length >= 3 && /^[A-Za-z]/.test(rest)) {
        return { word: trimmed.slice(0, token.length), rest: rest.trim(), smushed: true };
      }
    }
  }
  return null;
}

/**
 * Build the alternate "suffix moved to street line" form. Uses the canonical
 * Title-Case form of the suffix.
 *   buildAlternateStreetWithSuffix("123 MAIN", "ST") -> "123 MAIN St"
 *   buildAlternateStreetWithSuffix("",         "Blvd") -> "Blvd"
 */
function buildAlternateStreetWithSuffix(street: string, suffixWord: string): string {
  const canonical =
    STREET_SUFFIX_CANONICAL[suffixWord.toLowerCase()] ??
    (suffixWord.charAt(0).toUpperCase() + suffixWord.slice(1).toLowerCase());
  const trimmed = street.trim();
  if (!trimmed) return canonical;
  return `${trimmed} ${canonical}`;
}

/**
 * Post-process an AI-extracted address object.
 *
 * @example Wyoming swap:
 *   postProcessAddress({ streetAddress: '', city: 'ANYSTREET\nANYTOWN',
 *                        state: 'WY', postalCode: '123',
 *                        rawContent: '123 ANYSTREET ANYTOWN, WY 82000' })
 *   // { kind: 'anomaly', reason: 'street/zip swap', rawContent: '...' }
 *
 * @example West Virginia state-merge:
 *   postProcessAddress({ streetAddress: '1 RHODODENDRON ROAD',
 *                        city: 'MOUNTAIN CITY', state: '',
 *                        postalCode: 'WV-23959' })
 *   // { kind: 'parsed', streetAddress: '1 RHODODENDRON ROAD',
 *   //   city: 'MOUNTAIN CITY', state: 'WV', postalCode: '23959',
 *   //   countryRegion: '' }
 *
 * @example Minneapolis directional ambiguity:
 *   postProcessAddress({ streetAddress: '123 MAIN STREET',
 *                        city: 'NORTHWEST MINNEAPOLIS', state: 'MN',
 *                        postalCode: '55401' })
 *   // { kind: 'parsed', ..., directionalAmbiguity: {
 *   //     leadingWord: 'Northwest', cityAsIs: 'NORTHWEST MINNEAPOLIS',
 *   //     alternateCity: 'Minneapolis',
 *   //     alternateAddress: '123 MAIN STREET NW',
 *   //     recommendedChoice: 'move-to-street' } }
 */
export function postProcessAddress(input: AiAddressInput): PostProcessResult {
  const street = (input.streetAddress ?? '').trim();
  let city = (input.city ?? '').trim();
  let state = (input.state ?? '').trim();
  let postalCode = (input.postalCode ?? '').trim();
  const countryRegion = (input.countryRegion ?? '').trim();

  // Anomaly 1: Wyoming-style street/zip swap
  const cityHasNewline = /\n/.test(city);
  const zipIsShortNumeric = /^\d{1,4}$/.test(postalCode);
  if (!street && cityHasNewline && zipIsShortNumeric) {
    return {
      kind: 'anomaly',
      reason: 'street/zip swap (street number parsed as ZIP; street/city merged)',
      rawContent: (input.rawContent ?? '').trim(),
    };
  }

  // Anomaly 2: West Virginia state embedded in ZIP
  if (!state) {
    const mergedMatch = postalCode.match(/^([A-Z]{2})-(\d{5}(?:-?\d{4})?)$/);
    if (mergedMatch) {
      state = mergedMatch[1]!;
      postalCode = mergedMatch[2]!;
    }
  }

  // ZIP format normalization (applies to all successfully-parsed addresses)
  postalCode = normalizeZip(postalCode);

  // Directional-prefixed city ambiguity detection (non-destructive).
  let directionalAmbiguity: DirectionalAmbiguity | undefined;
  const detected = detectLeadingDirectional(city);
  if (detected) {
    const normalizedWord = titleCaseBasic(detected.word);
    const alternateCity = titleCaseBasic(detected.rest);
    const titleCasedCityAsIs = titleCaseBasic(city);
    const recommendedChoice: 'keep-as-is' | 'move-to-street' =
      KNOWN_DIRECTIONAL_CITIES.has(titleCasedCityAsIs) ? 'keep-as-is' : 'move-to-street';
    directionalAmbiguity = {
      leadingWord: normalizedWord,
      cityAsIs: city,
      alternateCity,
      alternateAddress: buildAlternateAddress(street, detected.word),
      recommendedChoice,
    };
  }

  // Street-suffix bleed detection — only run if no directional ambiguity
  // was flagged (the two are mutually exclusive in practice; directional
  // tokens like N/S/E/W don't overlap with suffix tokens like St/Ave/Blvd).
  let streetSuffixAmbiguity: StreetSuffixAmbiguity | undefined;
  if (!directionalAmbiguity) {
    const suffixDetected = detectLeadingStreetSuffix(city);
    if (suffixDetected) {
      const cityAsIs = titleCaseBasic(city);
      const alternateCity = titleCaseBasic(suffixDetected.rest);
      // Saint-city detection: "St Joseph", "STJOSEPH" both compare-equal.
      const cityKey = cityAsIs.toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ').trim();
      const cityKeyNoSpace = cityKey.replace(/\s+/g, '');
      const isSaintCity =
        suffixDetected.word.toLowerCase() === 'st' &&
        (KNOWN_SAINT_CITIES.has(cityKey) || KNOWN_SAINT_CITIES.has(cityKeyNoSpace));
      const recommendedChoice: 'keep-as-is' | 'move-to-street' =
        isSaintCity ? 'keep-as-is' : 'move-to-street';
      streetSuffixAmbiguity = {
        leadingWord: suffixDetected.word,
        smushed: suffixDetected.smushed,
        cityAsIs,
        alternateCity,
        alternateAddress: buildAlternateStreetWithSuffix(street, suffixDetected.word),
        recommendedChoice,
      };
    }
  }

  const result: ParsedAddress = {
    kind: 'parsed',
    streetAddress: street,
    city,
    state,
    postalCode,
    countryRegion,
  };
  if (directionalAmbiguity) result.directionalAmbiguity = directionalAmbiguity;
  if (streetSuffixAmbiguity) result.streetSuffixAmbiguity = streetSuffixAmbiguity;
  return result;
}
